/**
 * Centralized Date & Timezone Utilities for CK Pulse
 * Ensures consistent UTC parsing across SQLite datetimes ("YYYY-MM-DD HH:MM:SS")
 * and ISO-8601 strings ("YYYY-MM-DDTHH:MM:SS.sssZ"), formatting accurately
 * in the organization's or user's configured timezone (e.g. Asia/Kolkata UTC+5:30).
 */

export const DEFAULT_TIMEZONE = 'Asia/Kolkata';

export const COMMON_TIMEZONES = [
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST +05:30)', offset: '+05:30' },
  { value: 'UTC', label: 'UTC (+00:00)', offset: '+00:00' },
  { value: 'America/New_York', label: 'America/New_York (EST/EDT -05:00/-04:00)', offset: '-05:00' },
  { value: 'America/Chicago', label: 'America/Chicago (CST/CDT -06:00/-05:00)', offset: '-06:00' },
  { value: 'America/Denver', label: 'America/Denver (MST/MDT -07:00/-06:00)', offset: '-07:00' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT -08:00/-07:00)', offset: '-08:00' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST +00:00/+01:00)', offset: '+00:00' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST +01:00/+02:00)', offset: '+01:00' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST +01:00/+02:00)', offset: '+01:00' },
  { value: 'Africa/Dar_es_Salaam', label: 'Africa/Dar_es_Salaam (EAT +03:00)', offset: '+03:00' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST +04:00)', offset: '+04:00' },
  { value: 'Asia/Karachi', label: 'Asia/Karachi (PKT +05:00)', offset: '+05:00' },
  { value: 'Asia/Dhaka', label: 'Asia/Dhaka (BST +06:00)', offset: '+06:00' },
  { value: 'Asia/Bangkok', label: 'Asia/Bangkok (ICT +07:00)', offset: '+07:00' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT +08:00)', offset: '+08:00' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST +09:00)', offset: '+09:00' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT +10:00/+11:00)', offset: '+10:00' },
];

/**
 * Get active application timezone from localStorage or fallback
 */
export function getActiveTimezone(orgTimezone?: string | null): string {
  if (orgTimezone && orgTimezone.trim()) return orgTimezone;
  if (typeof window !== 'undefined') {
    const cached = localStorage.getItem('pulse_tz');
    if (cached && cached.trim()) return cached;
  }
  return DEFAULT_TIMEZONE;
}

/**
 * Parse any date string safely into a JavaScript Date.
 * SQLite datetime("now") returns "YYYY-MM-DD HH:MM:SS" which is UTC without timezone indicators.
 * Passing this directly to new Date() causes browsers to interpret it as local time.
 * This parser normalizes SQLite and partial timestamps to true UTC Date objects.
 */
export function parseUtcDate(dateVal: string | number | Date | null | undefined): Date {
  if (!dateVal) return new Date();
  if (dateVal instanceof Date) return dateVal;
  if (typeof dateVal === 'number') return new Date(dateVal);

  let s = String(dateVal).trim();
  if (!s) return new Date();

  // Pattern: "2026-09-28 13:20:59" or "2026-09-28 13:20:59.123" (SQLite default datetime)
  if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/.test(s)) {
    s = s.replace(' ', 'T') + 'Z';
  } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(s)) {
    // ISO string without timezone offset
    s = s + 'Z';
  }

  const parsed = new Date(s);
  return isNaN(parsed.getTime()) ? new Date(dateVal) : parsed;
}

/**
 * Format time in active timezone (e.g. "06:50:59 PM")
 */
export function formatTimeInTz(
  dateVal: string | number | Date | null | undefined,
  timeZone?: string | null,
  options?: Intl.DateTimeFormatOptions
): string {
  const d = parseUtcDate(dateVal);
  const tz = getActiveTimezone(timeZone);
  try {
    return d.toLocaleTimeString([], {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      ...options,
    });
  } catch {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, ...options });
  }
}

/**
 * Format date in active timezone (e.g. "Sep 28, 2026")
 */
export function formatDateInTz(
  dateVal: string | number | Date | null | undefined,
  timeZone?: string | null,
  options?: Intl.DateTimeFormatOptions
): string {
  const d = parseUtcDate(dateVal);
  const tz = getActiveTimezone(timeZone);
  try {
    return d.toLocaleDateString([], {
      timeZone: tz,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...options,
    });
  } catch {
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric', ...options });
  }
}

/**
 * Format full date & time in active timezone (e.g. "Sep 28, 2026, 06:50:59 PM")
 */
export function formatDateTimeInTz(
  dateVal: string | number | Date | null | undefined,
  timeZone?: string | null,
  options?: Intl.DateTimeFormatOptions
): string {
  const d = parseUtcDate(dateVal);
  const tz = getActiveTimezone(timeZone);
  try {
    return d.toLocaleString([], {
      timeZone: tz,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      ...options,
    });
  } catch {
    return d.toLocaleString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      ...options,
    });
  }
}
